import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { usersService } from "../services/users.service";
import type { InternalUserResponse } from "../types/users-api.types";

export interface UserDetailState {
  user: InternalUserResponse | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * `GET /internal/users/:id`, read when the drawer opens.
 *
 * Keyed by id and guarded by a sequence ref, so a roster refresh behind the drawer cannot discard the detail
 * and a response for a previously-opened person cannot land in the panel of the current one.
 *
 * Worth knowing about what it returns: this is the ONLY read that carries `password_changed_at`, and it never
 * carries `roles` (the route has no `include`) nor the organization relation.
 *
 * Loading is derived from "the settled request key differs from the requested one" rather than set inside the
 * effect — a synchronous `setState` there is a cascading render.
 *
 * **Every open is a new request, including reopening the same person.** The key carries a generation that is
 * bumped whenever `userId` changes — and closing the drawer passes `null`, so close→reopen bumps it twice.
 * Without that, the second open matched the key of the FIRST fetch and the hook handed back the pre-edit
 * record in the very render where `open` flipped true. The drawer seeds its form from that first non-null
 * user and then refuses to re-seed, so the fields showed stale values while the header and the diff's
 * baseline showed fresh ones — and saving an unrelated field wiped whatever had changed in between.
 */
export function useUserDetail(userId: string | null): UserDetailState {
  const [entry, setEntry] = useState<{
    key: string;
    user: InternalUserResponse | null;
    error: string | null;
  }>({ key: "", user: null, error: null });
  const [request, setRequest] = useState<{ id: string | null; gen: number }>({ id: null, gen: 0 });
  const seqRef = useRef(0);

  // Adjusted during render — React's documented "reset state when a prop changes" pattern — so `requestKey`
  // is already correct in this very render and `isLoading` is true from the first frame instead of one late.
  let current = request;
  if (request.id !== userId) {
    current = { id: userId, gen: request.gen + 1 };
    setRequest(current);
  }

  const requestKey = userId ? `${userId}|${current.gen}` : "";

  useEffect(() => {
    if (!userId) return;

    const seq = ++seqRef.current;
    usersService
      .getById(userId)
      .then((data) => {
        if (seq !== seqRef.current) return;
        setEntry({ key: requestKey, user: data, error: null });
      })
      .catch((err: unknown) => {
        if (seq !== seqRef.current) return;
        setEntry({ key: requestKey, user: null, error: HttpApiService.getErrorMessage(err) });
      });

    return () => {
      seqRef.current += 1;
    };
  }, [userId, requestKey]);

  // Refetches the person currently open. A new generation invalidates the settled entry, so `user` goes null
  // and `isLoading` true until the response lands — the same path a fresh open takes.
  const reload = useCallback(() => setRequest((prev) => ({ ...prev, gen: prev.gen + 1 })), []);

  const settled = entry.key === requestKey;

  return useMemo(
    () => ({
      // Never hand back the previous person's record while the current one is still loading.
      user: settled ? entry.user : null,
      isLoading: Boolean(userId) && !settled,
      error: settled ? entry.error : null,
      reload,
    }),
    [settled, entry.user, entry.error, userId, reload],
  );
}
